import { useMemo } from "react";

import { Appointment } from "../AppointmentPreviewCard/types";

/**
 * Maps the appointment's current offerings, packages and add-ons to the
 * initial state of useCenterServiceOptions.
 *
 * initialServices is memoized on packages/offerings only (not the whole
 * appointment object) — useCenterServiceOptions depends on its identity.
 */
const useInitialSelection = (appointment: Appointment) => {
  const initialServices = useMemo(() => {
    const centerPackages = appointment.packages.map((pkg) => ({
      id: pkg.centerPackageId,
      addOnIds: pkg.addOns.map((addOn) => addOn.centerAddOnId),
      addOnQuantities: Object.fromEntries(
        pkg.addOns.map((addOn) => [addOn.centerAddOnId, addOn.quantity]),
      ),
      studyIds: pkg.offerings.map((study) => study.id),
    }));

    const centerOfferings = appointment.offerings.map((offering) => ({
      id: offering.centerOfferingId,
      side: offering.side,
      addOnIds: offering.addOns?.map((addOn) => addOn.centerAddOnId) || [],
      addOnQuantities: Object.fromEntries(
        (offering.addOns || []).map((addOn) => [
          addOn.centerAddOnId,
          addOn.quantity,
        ]),
      ),
      studyIds: [offering.id],
    }));

    return [...centerPackages, ...centerOfferings];
  }, [appointment.packages, appointment.offerings]);

  const initialAddOnsIds = appointment.addOns.map(
    (addOn) => addOn.centerAddOnId,
  );

  const initialAddOnQuantities = Object.fromEntries(
    appointment.addOns.map((addOn) => [addOn.centerAddOnId, addOn.quantity]),
  );

  return { initialServices, initialAddOnsIds, initialAddOnQuantities };
};

export default useInitialSelection;
